import {
	KPI_METRIC_KEYS_LIST,
	METRICS_LABELS,
	TOUCHPOINT_OPTIONS,
} from "../constants.ts";

export type MetricKey = (typeof KPI_METRIC_KEYS_LIST)[number];
export type ChannelKey = (typeof TOUCHPOINT_OPTIONS)[number]["value"];

export const ALLOWED_METRIC_KEYS = [...KPI_METRIC_KEYS_LIST] as MetricKey[];
export const ALLOWED_CHANNEL_KEYS = TOUCHPOINT_OPTIONS.map(
	(option) => option.value,
) as ChannelKey[];

const METRIC_KEY_SET = new Set<string>(ALLOWED_METRIC_KEYS);
const CHANNEL_KEY_SET = new Set<string>(ALLOWED_CHANNEL_KEYS);

const CHANNEL_LABELS: Record<string, string> = Object.fromEntries(
	TOUCHPOINT_OPTIONS.map((option) => [option.value, option.label]),
);

function normalizeOptionalString(
	value: string | null | undefined,
): string | null {
	if (value === null || value === undefined) {
		return null;
	}

	const trimmed = value.trim();
	return trimmed.length > 0 ? trimmed : null;
}

function normalizeDistinctSorted(values: string[]): string[] {
	const unique = Array.from(new Set(values));
	unique.sort();
	return unique;
}

function parseJsonArrayString(raw: string, fieldName: string): unknown[] {
	try {
		const parsed: unknown = JSON.parse(raw);
		if (!Array.isArray(parsed)) {
			throw new Error(`${fieldName} must be a JSON array string.`);
		}
		return parsed;
	} catch (error) {
		if (
			error instanceof Error &&
			error.message === `${fieldName} must be a JSON array string.`
		) {
			throw error;
		}
		throw new Error(`${fieldName} must be a JSON array string.`);
	}
}

function validateListItems(
	values: readonly unknown[],
	fieldName: string,
	keySet: Set<string>,
	keyKind: "metric" | "channel",
): string[] {
	const normalized: string[] = [];
	for (let index = 0; index < values.length; index += 1) {
		const raw = values[index];
		if (typeof raw !== "string") {
			throw new Error(
				`${fieldName}[${index}] must be a non-empty ${keyKind} key string.`,
			);
		}

		const key = raw.trim();
		if (key.length === 0) {
			throw new Error(
				`${fieldName}[${index}] must be a non-empty ${keyKind} key string.`,
			);
		}

		if (!keySet.has(key)) {
			throw new Error(`Unknown ${keyKind} key '${key}' in ${fieldName}.`);
		}

		normalized.push(key);
	}

	return normalizeDistinctSorted(normalized);
}

function validateSingleKey(
	value: string,
	fieldName: string,
	keySet: Set<string>,
	keyKind: "metric" | "channel",
): string {
	const key = value.trim();
	if (key.length === 0) {
		throw new Error(`${fieldName} must be a non-empty ${keyKind} key string.`);
	}

	if (!keySet.has(key)) {
		throw new Error(`Unknown ${keyKind} key '${key}' in ${fieldName}.`);
	}

	return key;
}

function toNullableList(values: string[]): string[] | null {
	return values.length > 0 ? values : null;
}

export function parseOptionalMetricKeysJsonArrayString(
	raw: string | null | undefined,
	fieldName = "requested_metrics",
): MetricKey[] | null {
	const normalized = normalizeOptionalString(raw);
	if (!normalized) {
		return null;
	}

	const values = parseJsonArrayString(normalized, fieldName);
	return toNullableList(
		validateListItems(values, fieldName, METRIC_KEY_SET, "metric"),
	) as MetricKey[] | null;
}

export function parseOptionalChannelKeysJsonArrayString(
	raw: string | null | undefined,
	fieldName = "channels",
): ChannelKey[] | null {
	const normalized = normalizeOptionalString(raw);
	if (!normalized) {
		return null;
	}

	const values = parseJsonArrayString(normalized, fieldName);
	return toNullableList(
		validateListItems(values, fieldName, CHANNEL_KEY_SET, "channel"),
	) as ChannelKey[] | null;
}

export function validateMetricKeysList(
	values: readonly string[] | null | undefined,
	fieldName = "requested_metrics",
): MetricKey[] | null {
	if (!values || values.length === 0) {
		return null;
	}

	return toNullableList(
		validateListItems(values, fieldName, METRIC_KEY_SET, "metric"),
	) as MetricKey[] | null;
}

export function validateChannelKeysList(
	values: readonly string[] | null | undefined,
	fieldName = "channels",
): ChannelKey[] | null {
	if (!values || values.length === 0) {
		return null;
	}

	return toNullableList(
		validateListItems(values, fieldName, CHANNEL_KEY_SET, "channel"),
	) as ChannelKey[] | null;
}

export function parseOptionalMetricKey(
	raw: string | null | undefined,
	fieldName = "sort_by",
): MetricKey | null {
	const normalized = normalizeOptionalString(raw);
	if (!normalized) {
		return null;
	}

	return validateSingleKey(
		normalized,
		fieldName,
		METRIC_KEY_SET,
		"metric",
	) as MetricKey;
}

export function parseRequiredChannelKey(
	raw: string,
	fieldName = "channel",
): ChannelKey {
	const normalized = normalizeOptionalString(raw);
	if (!normalized) {
		throw new Error(`${fieldName} must be a non-empty channel key string.`);
	}

	return validateSingleKey(
		normalized,
		fieldName,
		CHANNEL_KEY_SET,
		"channel",
	) as ChannelKey;
}

const METRICS_GLOSSARY_ITEMS = ALLOWED_METRIC_KEYS.map((key) => {
	const label = METRICS_LABELS[key] ?? key;
	return `- ${key} -> ${label}`;
});

const CHANNELS_GLOSSARY_ITEMS = ALLOWED_CHANNEL_KEYS.map((key) => {
	const label = CHANNEL_LABELS[key] ?? key;
	return `- ${key} -> ${label}`;
});

export const TOOL_INPUT_FORMAT_GUIDE = [
	"Tool input formats:",
	'- requested_metrics: JSON array string of metric keys (example: ["customer_revenue","total_spend"])',
	'- channels: JSON array string of channel keys (example: ["meta","google"])',
	"- sort_by: plain metric key string (example: customer_revenue)",
	"- channel: plain channel key string (example: meta)",
].join("\n");

export const METRICS_GLOSSARY_PROMPT = [
	"Metrics glossary (keys -> meaning):",
	...METRICS_GLOSSARY_ITEMS,
].join("\n");

export const CHANNELS_GLOSSARY_PROMPT = [
	"Channels glossary (keys -> meaning):",
	...CHANNELS_GLOSSARY_ITEMS,
].join("\n");
